import { Navigation } from 'lucide-react';
import type { ConfigSource, UnitLocation } from '@vtak/shared';
import { useMapStore } from '@/stores/map-store';
import { useLocationsQuery } from '@/queries/use-location-queries';
import { cn } from '@/lib/utils';

/** Dot color + label per config source (status is never conveyed by color alone). */
const SOURCE_META: Record<ConfigSource, { dot: string; label: string }> = {
  live: { dot: 'bg-emerald-400', label: 'LIVE' },
  cached: { dot: 'bg-amber-400', label: 'CACHED' },
  degraded: { dot: 'bg-red-500', label: 'DEGRADED' },
};

/** Config-source dot + text label. */
export function ConfigSourceTag({ configSource }: { configSource: ConfigSource }): React.JSX.Element {
  const source = SOURCE_META[configSource];
  return (
    <span className="flex items-center gap-1" title={`Config source: ${source.label}`}>
      <span className={cn('h-1.5 w-1.5 rounded-full', source.dot)} />
      <span className="font-mono text-[9px] tracking-wide text-muted-foreground">{source.label}</span>
    </span>
  );
}

/** Quick-jump list of the active unit's locations (flies the map on click). */
export function LocationJumpList({
  unitId,
  onJump,
}: {
  unitId: string;
  onJump: () => void;
}): React.JSX.Element {
  const query = useLocationsQuery(unitId);
  const setFlyTo = useMapStore((s) => s.setFlyTo);
  const locations = query.data ?? [];

  if (locations.length === 0) {
    return <p className="px-3 py-3 text-center text-[11px] text-muted-foreground">No locations.</p>;
  }

  function jump(loc: UnitLocation): void {
    setFlyTo({ lat: loc.lat, lng: loc.lng, zoom: loc.zoom });
    onJump();
  }

  return (
    <div className="max-h-[50vh] space-y-0.5 overflow-y-auto p-1.5">
      {locations.map((loc) => (
        <button
          key={loc.id}
          type="button"
          onClick={() => jump(loc)}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-echo-border/30"
        >
          <Navigation className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">{loc.name}</span>
        </button>
      ))}
    </div>
  );
}
